"use client";

import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import * as d3 from "d3";
import classNames from "classnames";
import { DEFAULT_COLORS } from "@/constants/colors";
import { BubbleChartProps, BubbleChartDataItem, BubbleNode } from "@/modules/widgets/types/BubbleChartWidget";
import { NoDataView } from "../NoDataView";

const defaultValueFormatter = (value: number) => value.toLocaleString();
const toId = (label: string) => label.toLowerCase().replace(/ |\//g, "-");
/**
 * Wraps label text into lines that fit within a given pixel width.
 * Uses character-width estimation based on font size.
 */
const MAX_LABEL_LINES = 2;
const wrapTextToFit = (label: string, maxWidth: number, fontSize: number, maxLines = MAX_LABEL_LINES): string[] => {
    const charWidth = fontSize * 0.55;
    const maxChars = Math.max(4, Math.floor(maxWidth / charWidth));
    const words = label.split(/\s+/);
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
        const candidate = currentLine ? `${currentLine} ${word}` : word;
        if (candidate.length <= maxChars) {
            currentLine = candidate;
        } else {
            if (currentLine) {
                lines.push(currentLine);
                if (lines.length >= maxLines) {
                    // Truncate last line with ellipsis
                    const last = lines[lines.length - 1];
                    lines[lines.length - 1] = last.length > maxChars ? `${last.substring(0, maxChars - 1)}…` : `${last}…`;
                    return lines;
                }
            }
            currentLine = word.length > maxChars ? `${word.substring(0, maxChars - 1)}…` : word;
        }
    }
    if (currentLine) {
        if (lines.length >= maxLines) {
            const last = lines[lines.length - 1];
            lines[lines.length - 1] = last.length > maxChars ? `${last.substring(0, maxChars - 1)}…` : `${last}…`;
        } else {
            lines.push(currentLine);
        }
    }
    return lines;
};

interface HoverCardState {
    node: BubbleNode;
    x: number;
    y: number;
}

interface ExtendedBubbleChartProps extends BubbleChartProps {
    hiddenSeries?: Set<string>;
}

export default function BubbleChart({
    data,
    colors = DEFAULT_COLORS,
    showLegend = true,
    valueFormatter = defaultValueFormatter,
    padding = 8,
    hiddenSeries = new Set<string>(),
    hoverExpand = true,
    minBubbleRadius = 0,
}: ExtendedBubbleChartProps) {
    const minHeight = 380;
    const [hoveredBubble, setHoveredBubble] = useState<string | null>(null);
    const [hoverCard, setHoverCard] = useState<HoverCardState | null>(null);
    const [dimensions, setDimensions] = useState({ width: 300, height: minHeight });
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const observer = new ResizeObserver((entries) => {
            const { width, height } = entries[0].contentRect;
            if (width > 0) {
                setDimensions({ width, height: Math.max(height, minHeight) });
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const handleBubbleEnter = useCallback((node: BubbleNode, e: React.MouseEvent) => {
        setHoveredBubble(node.id);
        const svgEl = svgRef.current;
        if (!svgEl) return;
        const rect = svgEl.getBoundingClientRect();
        const svgScale = rect.width / dimensions.width;
        const cardX = rect.left + node.x * svgScale;
        const cardY = rect.top + (node.y - node.r) * svgScale;
        setHoverCard({ node, x: cardX, y: cardY });
    }, [dimensions.width]);

    const handleBubbleLeave = useCallback(() => {
        setHoveredBubble(null);
        setHoverCard(null);
    }, []);

    const chartData = data?.data || [];
    const hasError = !!data?.error;
    const isEmpty = !Array.isArray(chartData) || chartData.length === 0;
    const removeKeyword = data?.metadata?.removeKeyword || "";
    const effectiveShowLegend = data?.metadata?.hideSeries === true ? false : showLegend;

    /**
     * Computes bubble positions and sizes using D3's circle packing algorithm.
     * Layout is stable — does NOT re-run on hover.
     */
    // Reduced from 1.2 → 1.05: subtle grow keeps animation without edge overflow.
    const HOVER_SCALE = 1.05;
    const { bubbleNodes, dataWithColors } = useMemo(() => {
        const emptyResult = {
            bubbleNodes: [] as BubbleNode[],
            dataWithColors: [] as (BubbleChartDataItem & { color: string })[],
        };

        if (isEmpty) return emptyResult;

        const processedData = chartData.map((item) => ({
            ...item,
            label: removeKeyword ? item.label.replace(new RegExp(removeKeyword, "gi"), "").trim() : item.label,
        }));

        const withColors = processedData.map((item, index) => ({
            ...item,
            color: item.color || colors[index % colors.length],
        }));

        // Filter out hidden series and zero-value items
        const visibleData = withColors.filter((item) => !hiddenSeries.has(item.label) && item.value > 0);
        const filteredWithColors = withColors.filter((item) => item.value > 0);
        if (visibleData.length === 0) return { ...emptyResult, dataWithColors: withColors };

        const maxValue = Math.max(...visibleData.map((d) => d.value));
        const minValue = Math.min(...visibleData.map((d) => d.value));
        const ratio = maxValue / Math.max(minValue, 1);

        // Apply sqrt scaling when ratio exceeds 8x to prevent tiny bubbles.
        // Use a relative floor (sqrt(max)/5) so large bubbles aren't incorrectly clamped.
        const sqrtFloor = Math.sqrt(maxValue) / 5;
        let scaledData = visibleData.map((item) => ({
            ...item,
            scaledValue: ratio > 8 ? Math.max(sqrtFloor, Math.sqrt(item.value)) : item.value,
        }));

        // Enforce minBubbleRadius by boosting small scaledValues before D3 layout.
        // D3 radius ≈ sqrt(value / totalValue) * packRadius, so to guarantee r >= minBubbleRadius:
        //   value >= (minBubbleRadius / packRadius)^2 * totalValue
        const packSize = dimensions.width;
        if (minBubbleRadius > 0) {
            const packRadius = packSize / 2;
            const totalScaled = scaledData.reduce((s, d) => s + d.scaledValue, 0);
            const minScaledForRadius = (minBubbleRadius / packRadius) ** 2 * totalScaled;
            scaledData = scaledData.map((item) => ({
                ...item,
                scaledValue: Math.max(minScaledForRadius, item.scaledValue),
            }));
        }

        const pack = d3
            .pack<{ children: typeof scaledData }>()
            .size([packSize, packSize])
            .padding(padding);

        const root = d3
            .hierarchy({ children: scaledData })
            .sum((d: any) => d.scaledValue || d.value)
            .sort((a, b) => (b.value || 0) - (a.value || 0));

        const nodes = pack(root as any).leaves();

        const bubbles: BubbleNode[] = nodes.map((node: any) => ({
            id: toId(node.data.label),
            label: node.data.label,
            value: node.data.value,
            color: node.data.color,
            x: node.x,
            y: node.y,
            r: node.r,
        }));

        return { bubbleNodes: bubbles, dataWithColors: filteredWithColors };
    }, [chartData, colors, hiddenSeries, dimensions, padding, isEmpty, removeKeyword, minBubbleRadius]);

    if (hasError) {
        return <div className="py-5 px-10 text-center text-red-500">Error: {data?.error}</div>;
    }

    if (isEmpty) {
        return <NoDataView />;
    }

    // Check if all data values are 0, null, undefined, or non-numeric
    const hasMeaningfulData = chartData.some((item) => item.value != null && item.value !== 0 && typeof item.value === "number");

    if (!hasMeaningfulData) {
        return <NoDataView />;
    }

    return (
        <div className="w-full h-full overflow-hidden">
            <div className="flex flex-col lg:flex-row gap-4 h-full">
                <div className={classNames("w-full", effectiveShowLegend && "lg:w-2/3")}>
                    <div ref={containerRef} className="w-full h-full" style={{ minHeight }}>
                        {(() => {
                            // Expand the viewBox by a small margin on all sides so that edge bubbles
                            // scaled at HOVER_SCALE never exceed the SVG viewport boundary.
                            // margin >= maxR * (HOVER_SCALE - 1) guarantees no clipping.
                            const maxBubbleR = bubbleNodes.length > 0 ? Math.max(...bubbleNodes.map(n => n.r)) : 0;
                            const vbMargin = Math.ceil(maxBubbleR * (HOVER_SCALE - 1)) + 4;
                            const vbSize = dimensions.width + 2 * vbMargin;
                            return (
                        <svg
                            ref={svgRef}
                            width="100%"
                            height="100%"
                            viewBox={`${-vbMargin} ${-vbMargin} ${vbSize} ${vbSize}`}
                            preserveAspectRatio="xMidYMid meet"
                            style={{ fontFamily: "Roboto, sans-serif" }}
                        >
                            <defs>
                                <filter id="bubble-shadow" x="-20%" y="-20%" width="140%" height="140%">
                                    <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#00000033" />
                                </filter>
                            </defs>
                            <g>
                                {(() => {
                                    // Pre-compute hovered node's dynamic scale
                                    let hoveredDynScale = HOVER_SCALE;
                                    const hNode = hoverExpand && hoveredBubble
                                        ? bubbleNodes.find((n) => n.id === hoveredBubble)
                                        : null;
                                    if (hNode) {
                                        const lfs = Math.max(6, Math.min(12, hNode.r / 5));
                                        const baseLines = wrapTextToFit(hNode.label, hNode.r * HOVER_SCALE * 1.4, lfs, 3);
                                        if (baseLines.some((l) => l.endsWith("…"))) {
                                            for (let s = HOVER_SCALE; s <= HOVER_SCALE; s += 0.05) {
                                                const tryLines = wrapTextToFit(hNode.label, hNode.r * s * 1.4, lfs, 3);
                                                if (!tryLines.some((l) => l.endsWith("…"))) {
                                                    hoveredDynScale = s;
                                                    break;
                                                }
                                                hoveredDynScale = s;
                                            }
                                        }
                                    }

                                    return bubbleNodes.map((node) => {
                                        const isHovered = hoveredBubble === node.id;
                                        const displayR = node.r;

                                        // Push neighbors away from hovered bubble
                                        let offsetX = 0;
                                        let offsetY = 0;
                                        if (hoverExpand && hoveredBubble && !isHovered && hNode) {
                                            const dx = node.x - hNode.x;
                                            const dy = node.y - hNode.y;
                                            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
                                            const pushAmount = hNode.r * (hoveredDynScale - 1) * 1.2;
                                            offsetX = (dx / dist) * pushAmount;
                                            offsetY = (dy / dist) * pushAmount;
                                        }

                                        // Font sizes based on base radius
                                        const valueFontSize = Math.max(8, Math.min(22, displayR / 3));
                                        const labelFontSize = Math.max(6, Math.min(12, displayR / 5));

                                        const effectiveScale = isHovered && hoverExpand ? hoveredDynScale : 1;
                                        const expandedR = isHovered && hoverExpand ? node.r * hoveredDynScale : node.r;
                                        const textWidth = expandedR * 1.4;
                                        const labelLines = wrapTextToFit(node.label, textWidth, labelFontSize, isHovered && hoverExpand ? 3 : MAX_LABEL_LINES);
                                        const lineHeight = labelFontSize * 1.25;
                                        const gap = Math.max(2, displayR / 14);
                                        const totalLabelHeight = labelLines.length * lineHeight;
                                        const totalHeight = valueFontSize + gap + totalLabelHeight;
                                        const startY = -totalHeight / 2;

                                        return (
                                            <g
                                                key={node.id}
                                                onMouseEnter={(e) => handleBubbleEnter(node, e)}
                                                onMouseLeave={handleBubbleLeave}
                                                className="cursor-pointer"
                                                style={{
                                                    transform: `translate(${node.x + offsetX}px, ${node.y + offsetY}px)`,
                                                    transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                                                }}
                                            >
                                                {/* Inner g scales circle + text together so text grows with bubble */}
                                                <g
                                                    style={{
                                                        transform: `scale(${effectiveScale})`,
                                                        transformOrigin: "0 0",
                                                        transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                                                    }}
                                                >
                                                    <circle
                                                        r={node.r}
                                                        fill={node.color}
                                                        filter={isHovered && hoverExpand ? "url(#bubble-shadow)" : undefined}
                                                        style={{
                                                            transition: "opacity 0.4s ease, filter 0.3s ease",
                                                            opacity: hoverExpand && hoveredBubble && !isHovered ? 0.55 : 1,
                                                        }}
                                                    />
                                                    <text
                                                        textAnchor="middle"
                                                        y={startY}
                                                        dominantBaseline="hanging"
                                                        fill="white"
                                                        className="font-semibold"
                                                        fontSize={valueFontSize}
                                                    >
                                                        {valueFormatter(node.value)}
                                                    </text>
                                                    {labelLines.map((line: string, idx: number) => (
                                                        <text
                                                            key={idx}
                                                            textAnchor="middle"
                                                            y={startY + valueFontSize + gap + idx * lineHeight}
                                                            dominantBaseline="hanging"
                                                            fill="white"
                                                            className="font-normal"
                                                            fontSize={labelFontSize}
                                                            opacity={displayR >= 25 ? 1 : 0}
                                                        >
                                                            {line}
                                                        </text>
                                                    ))}
                                                </g>
                                            </g>
                                        );
                                    });
                                })()}
                            </g>
                        </svg>
                            );
                        })()}
                    </div>
                </div>

                {effectiveShowLegend && (
                    <div className="w-full lg:w-1/3 pr-6">
                        <ul className="space-y-0.5 max-h-72 overflow-y-auto">
                            {dataWithColors.map((item) => {
                                const isHidden = hiddenSeries.has(item.label);
                                return (
                                    <li
                                        key={item.label}
                                        className={classNames(
                                            "flex items-center gap-3 py-2.5 px-3 rounded-lg select-none transition-all duration-200",
                                            isHidden ? "opacity-50 bg-gray-50 hover:bg-gray-100" : "hover:bg-gray-50"
                                        )}
                                        onMouseEnter={() => setHoveredBubble(toId(item.label))}
                                        onMouseLeave={() => setHoveredBubble(null)}
                                    >
                                        <span
                                            className={classNames("w-2.5 h-2.5 rounded-sm shrink-0", isHidden && "opacity-30")}
                                            style={{ backgroundColor: item.color }}
                                        />
                                        <span className={classNames("truncate text-sm", isHidden ? "text-gray-400" : "text-gray-900")}>{item.label}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>

            {hoverCard && (
                <div
                    style={{
                        position: "fixed",
                        left: hoverCard.x,
                        top: hoverCard.y - 8,
                        transform: "translateX(-50%) translateY(-100%)",
                        zIndex: 9999,
                        pointerEvents: "none",
                    }}
                >
                    <div className="bg-white px-3 py-2 border border-gray-200 shadow-lg rounded-md">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: hoverCard.node.color }} />
                            <span className="font-medium text-gray-900 text-sm">{hoverCard.node.label}</span>
                        </div>
                        <div className="font-semibold text-sm text-gray-900">{valueFormatter(hoverCard.node.value)}</div>
                    </div>
                </div>
            )}
        </div>
    );
}

export { BubbleChart };
